export type Point = { x: number; y: number };
export type FloorBand = { xMin: number; xMax: number; yMin: number; yMax: number };

export const FLOOR_BAND: FloorBand = { xMin: 12, xMax: 78, yMin: 58, yMax: 90 };
export const WALK_SPEED = 5.5;
export const RUN_SPEED = 9.5;
export const RUN_DIST = 22;
export const MIN_SEGMENT_MS = 700;
export const IDLE_MS_MIN = 1800;
export const IDLE_MS_MAX = 4200;
export const REDUCED_WALK_SPEED = 2.5;

export function clampToBand(p: Point, band: FloorBand = FLOOR_BAND): Point {
  return {
    x: Math.min(band.xMax, Math.max(band.xMin, p.x)),
    y: Math.min(band.yMax, Math.max(band.yMin, p.y)),
  };
}

export function pickWanderTarget(from: Point, rng: () => number, band: FloorBand = FLOOR_BAND): Point {
  let last = from;
  for (let attempt = 0; attempt < 10; attempt++) {
    const candidate = clampToBand({
      x: band.xMin + rng() * (band.xMax - band.xMin),
      y: band.yMin + rng() * (band.yMax - band.yMin),
    }, band);
    const dx = candidate.x - from.x;
    const dy = candidate.y - from.y;
    last = candidate;
    if (dx * dx + dy * dy > 120) return candidate;
  }
  return last;
}

export function easeInOutQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2;
}

export function lerp(from: Point, to: Point, t: number): Point {
  return {
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
  };
}

export function segment(
  from: Point,
  to: Point,
  opts?: { forceRun?: boolean; reducedMotion?: boolean; directed?: boolean },
): { motion: 'walk' | 'run'; durationMs: number } {
  const dist = Math.hypot(to.x - from.x, to.y - from.y);
  const reduced = Boolean(opts?.reducedMotion);
  const snap = reduced && (Boolean(opts?.forceRun) || Boolean(opts?.directed));
  const shouldRun = !reduced && (Boolean(opts?.forceRun) || dist >= RUN_DIST);
  const speed = reduced ? REDUCED_WALK_SPEED : shouldRun ? RUN_SPEED : WALK_SPEED;
  return {
    motion: shouldRun ? 'run' : 'walk',
    durationMs: snap ? MIN_SEGMENT_MS : Math.max(MIN_SEGMENT_MS, (dist / speed) * 1000),
  };
}

export function clientToStagePct(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
): Point {
  return {
    x: ((clientX - rect.left) / rect.width) * 100,
    y: ((clientY - rect.top) / rect.height) * 100,
  };
}

export function isShake(
  samples: Array<{ x: number; y: number; t: number }>,
  opts?: { windowMs?: number; minTravel?: number; minReversals?: number },
): boolean {
  const windowMs = opts?.windowMs ?? 400;
  const minTravel = opts?.minTravel ?? 48;
  const minReversals = opts?.minReversals ?? 2;
  if (samples.length === 0) return false;
  const lastT = samples[samples.length - 1]!.t;
  const windowed = samples.filter((s) => s.t >= lastT - windowMs);
  let travel = 0;
  let reversals = 0;
  let lastSign = 0;
  for (let i = 1; i < windowed.length; i++) {
    const prev = windowed[i - 1]!;
    const curr = windowed[i]!;
    const dx = curr.x - prev.x;
    const dy = curr.y - prev.y;
    travel += Math.hypot(dx, dy);
    const sign = Math.sign(dx);
    if (sign !== 0 && lastSign !== 0 && sign !== lastSign) reversals += 1;
    if (sign !== 0) lastSign = sign;
  }
  return travel >= minTravel && reversals >= minReversals;
}
